/**
 * ROLE-RULES.WIDGET
 * Périmètre utilisateurs et périmètre du tableau de bord d'un rôle
 */

import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { faInfoCircle, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { Role } from "@/redux/role.types"
import { Population } from "@/redux/population.types"
import { roleUpdateRules } from "@/redux/role.actions"
import Chip from "@/components/chip"
import Tooltip from "@/components/tooltip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SelectPopulationsModal from "@/modals/select-populations.modal"
import RoleScopeModal from "@/modals/role-scope.modal"
import RoleScopeChoiceModal from "@/modals/role-scope-choice.modal"

export default function RoleRulesWidget({ role }: { role: Role }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const accountName = useAppSelector((state) => state._session.accountName)
  const chipColor = useAppSelector((state) => state._session.accountColors.cta)
  const [modal, setModal] = useState<"conditions" | "scope" | "dynamic" | "fixed" | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  async function save(changes: Partial<Pick<Role, "conditions" | "scopeFilterNames" | "scopePopulations">>) {
    if (savingRef.current) return false
    savingRef.current = true
    setSaving(true)
    try {
      return Boolean(await dispatch(roleUpdateRules(role, changes)))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  function openModal(name: "conditions" | "scope") {
    if (saving || savingRef.current) return
    setModal(name)
  }

  return (
    <>
      {modal === "conditions" && (
        <SelectPopulationsModal
          title={t("roles_members")}
          subtitle={t("roles_members_help")}
          isWithoutDefaultFilters
          isSurveyFiltersIncluded
          selectedPopulations={role.conditions.map(
            (condition) => new Population({ ...condition, id: `${condition.filterName}_${condition.name}` })
          )}
          onClose={() => setModal(null)}
          onNext={(populations: Population[]) =>
            save({
              conditions: populations
                .filter((population) => population.filterName)
                .map((population) => ({ filterName: population.filterName!, name: population.name }))
            })
          }
        />
      )}
      {modal === "scope" && (
        <RoleScopeChoiceModal onClose={() => setModal(null)} onSelect={setModal} />
      )}
      {modal === "dynamic" && (
        <RoleScopeModal
          selectedNames={role.scopeFilterNames}
          onClose={() => setModal(null)}
          onSave={(scopeFilterNames) => save({ scopeFilterNames })}
        />
      )}
      {modal === "fixed" && (
        <SelectPopulationsModal
          title={t("roles_scope_fixed")}
          subtitle={t("roles_scope_fixed_help")}
          isWithoutDefaultFilters
          isSurveyFiltersIncluded
          selectedPopulations={role.scopePopulations.map(
            (population) => new Population({ ...population, id: `${population.filterName}_${population.name}` })
          )}
          onClose={() => setModal(null)}
          onNext={(populations: Population[]) => save({
            scopePopulations: populations
              .filter((population) => population.filterName)
              .map((population) => ({ filterName: population.filterName!, name: population.name }))
          })}
        />
      )}

      {/** Marge negative : le trait separateur va d'un bord a l'autre de la
          carte, le texte reste aligne sur son titre. */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 pb-8 mx-[-30px] px-[30px]"
        style={{ borderTop: "1px solid #F0F0F0" }}>
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{ margin: 0 }}>{t("roles_members")}</h3>
            <Tooltip text={t("roles_members_help")} width={280} className="!flex items-center leading-none" usePortal>
              <FontAwesomeIcon icon={faInfoCircle} className="grey-t cursor-help" aria-label={t("roles_members_help")} />
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {role.conditions.map((condition, index) => (
              <Chip
                key={JSON.stringify([condition.filterName, condition.name])}
                color={chipColor}
                isOutlineColored
                onDelete={
                  saving ? undefined : () => save({ conditions: role.conditions.filter((_, i) => i !== index) })
                }>
                {condition.filterName} : {condition.name}
              </Chip>
            ))}
            <Chip icon={faPlus} onClick={() => openModal("conditions")} />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{ margin: 0 }}>{t("roles_dashboard_scope")}</h3>
            <Tooltip text={t("roles_dashboard_scope_help")} width={280} className="!flex items-center leading-none" usePortal>
              <FontAwesomeIcon icon={faInfoCircle} className="grey-t cursor-help" aria-label={t("roles_dashboard_scope_help")} />
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/** Aucun attribut : le périmètre est l'organisation entière. */}
            {role.scopeFilterNames.length === 0 && role.scopePopulations.length === 0 && <Chip>{accountName}</Chip>}
            {role.scopeFilterNames.map((name) => (
              <Tooltip key={name} text={t("roles_scope_dynamic_help")} usePortal>
                <Chip
                  color="#ff9500"
                  isOutlineColored
                  onDelete={
                    saving
                      ? undefined
                      : () => save({ scopeFilterNames: role.scopeFilterNames.filter((item) => item !== name) })
                  }>
                  {name}
                </Chip>
              </Tooltip>
            ))}
            {role.scopePopulations.map((population, index) => (
              <Tooltip key={JSON.stringify([population.filterName, population.name])} text={t("roles_scope_fixed_help")} usePortal>
                <Chip
                  color={chipColor}
                  isOutlineColored
                  onDelete={saving ? undefined : () => save({
                    scopePopulations: role.scopePopulations.filter((_, i) => i !== index)
                  })}>
                  {population.filterName} : {population.name}
                </Chip>
              </Tooltip>
            ))}
            <Chip icon={faPlus} onClick={() => openModal("scope")} />
          </div>
        </section>
      </div>
    </>
  )
}
